import React, { FC, useCallback, useMemo, useState } from 'react';
import {
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    BundleOut
} from '../../store/api';
import BundleDiscountLine from './BundleDiscountLine';
import { AddToCartOrList } from '../common/AddToCartOrList/AddToCartOrList';
import { useTarget } from '../common/useTarget';
import { TargetSelectionListDropdown } from '../common/TargetSelectionListDropdown/TargetSelectionListDropdown';
import { useOrderLists } from '../common/useOrderLists';
import { PriceSelector } from '../search/PriceSelector';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useBundleDetailTotals } from '../../api/bundles/useBundleDetailTotals';
import { FetchBundleTotalsApiResponse } from '../../api/bundles/fetchBundleDetailTotals';
import { AddButton } from './AddButton';
import { PriceTier } from '../../../models/PriceTier';
import { useListContentChecks } from '../common/useListContentChecks';
import { LoanFormat, mapParsedLoanFormat } from '../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { AddOptions } from '../../../models/ListContentCheckOptions';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';

const getPriceData = (
    bundle: BundleOut,
    queryData: FetchBundleTotalsApiResponse | undefined
): FetchBundleTotalsApiResponse => {
    if (queryData) {
        return queryData;
    }

    const { price: basePrice, customerPrice: netValue, savedValue, savedPercentage, currency } = bundle;
    return { basePrice, netValue, savedValue, savedPercentage, currency };
};

type Props = {
    readonly bundle: BundleOut;
    readonly onAddToList: (
        bundleId: string,
        listId: string,
        priceTier: PriceTier,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onAddToCart: (
        bundleId: string,
        priceTier: PriceTier,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly loanFormat: LoanFormat;
};

export const BundleTileBottom: FC<Props> = ({ bundle, onAddToList, onAddToCart, loanFormat }) => {
    const { t } = useTranslation();
    const { target } = useTarget();
    const { orderlists, selectedOrderList, createNewList } = useOrderLists();
    const [priceTier, setPriceTier] = useState<PriceTier>({ lendingModel: 'OCOU', level: 'MIN' });
    const { data: bundleTotalsData, isInitialLoading: isLoading } = useBundleDetailTotals(bundle.bundleId, priceTier);

    const { bundleId, productAmount, productStockAmount } = bundle;
    const priceData = useMemo(() => getPriceData(bundle, bundleTotalsData), [bundle, bundleTotalsData]);
    const isCartTarget = target === ListActionTarget.Cart;

    const onAction = useCallback(
        (performChecks: boolean, options: AddOptions) => {
            if (options.target === ListActionTarget.List) {
                return onAddToList(bundleId!, options.list.id, priceTier, performChecks);
            } else {
                return onAddToCart(bundleId!, priceTier, performChecks);
            }
        },
        [onAddToList, onAddToCart, bundleId, priceTier]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<AddOptions>({ onAction });

    const checkBeforeAddingModalProps = getCheckBeforeAddingModalProps();

    const handleAddClick = useCallback(() => {
        changePrice(
            isCartTarget
                ? { target: ListActionTarget.Cart, priceTier }
                : { target: ListActionTarget.List, list: selectedOrderList!, priceTier }
        );
    }, [changePrice, priceTier, isCartTarget, selectedOrderList]);

    return (
        <div className="bndl-tile-bottom">
            <div className="column-left">
                <div className="amount">
                    {t(`loanFormat.titlesTextWithCount.${mapParsedLoanFormat(loanFormat).toLowerCase()}`, {
                        count: productAmount
                    })}
                </div>
                <div className="stocked">
                    {t('bundles.alreadyInYourLibrary', {
                        amount: t('numberFormat.numberWithoutDecimalPoint', { val: productStockAmount ?? 0 })
                    })}
                </div>
                <PriceSelector value={priceTier} onChange={setPriceTier} />
            </div>
            <div className="column-right">
                <AddToCartOrList />
                {!isCartTarget && (
                    <TargetSelectionListDropdown
                        orderlists={orderlists}
                        selectedOrderlist={selectedOrderList}
                        onCreate={createNewList}
                    />
                )}
                <AddButton
                    priceTier={priceTier}
                    target={target}
                    onClick={handleAddClick}
                    isLoading={isLoading || checkBeforeAddingModalProps.isLoading}
                    total={priceData.netValue}
                    currency={priceData.currency}
                />
                <BundleDiscountLine bundle={priceData} isLoading={isLoading} />
            </div>
            <CheckBeforeAddingModal {...checkBeforeAddingModalProps} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </div>
    );
};
